import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AngularImageViewerModule } from "@clarivate/angular-image-viewer";
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/internal/operators/finalize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { SurveyStatus } from 'src/app/enumeration/surveyStatus';
import { SurveyServices } from 'src/app/services/survey-services';
import { CheckAccessRoles } from 'src/app/utils/check-access-roles';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';

@Component({
  selector: 'app-survey-info',
  imports: [
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CommonModule,
    AngularImageViewerModule
  ],
  templateUrl: './survey-info.html',
  styleUrl: './survey-info.css',
})
export class SurveyInfo {

  @ViewChild('sectionTitleInput') sectionInputEl!: ElementRef<HTMLInputElement>;

  private apiService: SurveyServices = inject(SurveyServices);
  private checkAccessRoles: CheckAccessRoles = inject(CheckAccessRoles);
  private dateFormat: DateFormatPipe = inject(DateFormatPipe);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  surveyId = signal<any>(null);
  sectionId = signal<any>(null);
  questionId = signal<any>(null);
  questionTextMy = signal<any>(null);
  questionTextEng = signal<any>(null);

  groupedSections = signal<any[]>([]);
  ratings = signal<any[]>([]);

  sectionTitleId = signal<any>(null);
  sectionTitle = signal<string>('');
  subTitle = signal<string>('');
  description = signal<string>('');
  dealerLoginName = signal<any>(null);
  suggestions = signal<string>('');
  surveyInfoData = signal<any>({});

  isSuperAdminAndAdmin = signal<boolean>(false);
  isByDealer = signal<boolean>(false);
  isSurveySubmitted = signal<boolean>(false);

  isEditQuestionMode = signal<boolean>(false);
  isEditSectionMode = signal<boolean>(false);

  isSectionLoading = signal<boolean>(false);
  isQuestionLoading = signal<boolean>(false);
  isBtnLoading = signal<boolean>(false);

  // UI Interaction States
  isAddingSection = signal<boolean>(false);
  newSectionTitle = signal<string>('');
  apiLoading = signal<boolean>(false);

  editingGroupId = signal<number | null>(null);
  originalGroupTitleBackup = signal<string>('');
  isFormStatusOpen = signal<boolean>(false);
  isFormStatusActive = signal<boolean>(false);

  ngOnInit() {
    this.surveyId.set(this.route.snapshot.paramMap.get('surveyId'));
    this.dealerLoginName.set(this.route.snapshot.paramMap.get('loginName'));

    this.isSuperAdminAndAdmin.set(this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN]));

    this.getRatingInfoLst();

    if (this.dealerLoginName() !== 'unknown') {
      this.isByDealer.set(true);
      this.getSurveyInfoByDealer();
    } else {
      this.isByDealer.set(false);
      this.getSurveyInfo();
    }

  }

  //if dealer login name exist, get survey info by dealer, otherwise get survey info for super admin and admin
  getSurveyInfoByDealer() {
    this.apiService
      .getSurveyInfoByDealer(this.surveyId(), this.dealerLoginName())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const titleList = response?.Object?.sectionTitleInfoRespLst || [];

          this.surveyInfoData.set(response?.Object || {});
          this.suggestions.set(response?.Object?.suggestion || '');
          this.isFormStatusOpen.set(response.Object?.status === SurveyStatus.OPEN);
          this.isFormStatusActive.set(response.Object?.status === SurveyStatus.ACTIVE);

          this.isSurveySubmitted.set(titleList
            .flatMap((titleGroup: any) => titleGroup.sectionInfoRespLst || [])
            .flatMap((section: any) => section.questionInfoRespLst || [])
            .some((q: any) => q.ratingId != null)
          );

          this.groupedSections.set(titleList);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching survey info.');
        }
      });
  }

  //get rating info list for select option
  getRatingInfoLst() {
    this.apiService.getRatingInfoLst()
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response: any) => {
          this.ratings.set(response?.Object || []);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching rating list.');
        }
      });
  }

  //get survey info for super admin and admin
  getSurveyInfo() {
    this.apiService.getSurveyInfo(this.surveyId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const titleList = response?.Object?.sectionTitleInfoRespLst || [];

          this.surveyInfoData.set(response?.Object || {});
          this.suggestions.set(response?.Object?.suggestion || '');
          this.isFormStatusOpen.set(response.Object?.status === SurveyStatus.OPEN);
          this.isFormStatusActive.set(response.Object?.status === SurveyStatus.ACTIVE);

          this.isSurveySubmitted.set(titleList
            .flatMap((titleGroup: any) => titleGroup.sectionInfoRespLst || [])
            .flatMap((section: any) => section.questionInfoRespLst || [])
            .some((q: any) => q.ratingId != null)
          );

          this.groupedSections.set(titleList);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching survey info.');
        }
      });
  }

  startAddingSection(): void {
    this.isAddingSection.set(true);
    this.newSectionTitle.set('');

    // Automatically focuses text cursor on the text block frame
    setTimeout(() => {
      this.sectionInputEl?.nativeElement.focus();
    }, 50);
  }

  cancelAddingSection(): void {
    if (this.apiLoading()) return;
    this.isAddingSection.set(false);
    this.newSectionTitle.set('');
  }

  submitSectionTitle(): void {
    const title = this.newSectionTitle().trim();
    if (!title || this.apiLoading()) return;

    this.apiLoading.set(true);

    this.apiService.createTitle(this.surveyId(), title).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.apiLoading.set(false);
        this.isAddingSection.set(false);
      },
      error: (err: any) => {
        this.apiLoading.set(false);
        this.showError(err || 'Network error occurred while creating section title.');
      },
      complete: () => this.getSurveyInfo()
    });

    setTimeout(() => {
      this.apiLoading.set(false);
      this.isAddingSection.set(false);
      this.newSectionTitle.set('');
    }, 800);
  }

  startGroupTitleEdit(group: any): void {
    this.editingGroupId.set(group.id);
    this.originalGroupTitleBackup.set(group.title);
  }

  cancelGroupTitleEdit(): void {
    const currentEditingId = this.editingGroupId();
    if (currentEditingId) {
      const currentGroup = this.groupedSections().find(g => g.id === currentEditingId);
      if (currentGroup) {
        currentGroup.title = this.originalGroupTitleBackup(); // Revert back if canceled
      }
    }
    this.editingGroupId.set(null); // Close editor view seamlessly
  }

  submitGroupTitleUpdate(group: any): void {
    const cleanTitle = group.title?.trim();

    if (!cleanTitle) {
      this.showError('Title value text cannot be empty.');
      return;
    }

    // If the user didn't change anything, just close the editor box row interface
    if (this.originalGroupTitleBackup().trim() === cleanTitle) {
      this.editingGroupId.set(null);
      return;
    }

    this.apiLoading.set(true);

    // Invoke your API endpoint service layer profile directly
    this.apiService.updateTitle(group.id, cleanTitle).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.apiLoading.set(false);
        this.editingGroupId.set(null); // Lock editor row layout framework on success
        this.getSurveyInfo(); // Refresh data tree setup dynamically
      },
      error: (err: any) => {
        this.apiLoading.set(false);
        this.showError(err || 'Failed to update title.');
        this.cancelGroupTitleEdit(); // Revert model values state back to normal on failure
      }
    });
  }

  openCreateSectionModal(sectionTitle: any) {
    this.isEditSectionMode.set(false);
    this.sectionTitleId.set(sectionTitle.id);
    this.sectionTitle.set(sectionTitle.title);
    this.subTitle.set('');
    this.description.set('');
  }

  //create section title for super admin and admin
  createTitle() {

    let api
    if (this.isEditSectionMode()) {
      api = this.apiService.updateSectionTitle(this.sectionId(), this.sectionTitleId(), this.subTitle(), this.description());
    } else {
      api = this.apiService.createSectionTitle(this.surveyId(), this.sectionTitleId(), this.subTitle(), this.description());
    }

    this.isSectionLoading.set(true);
    api.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.showSuccess(
          this.isEditSectionMode()
            ? 'Section Updated Successfully'
            : 'Section Created Successfully'
        );
      },
      error: (err: any) => {
        this.isSectionLoading.set(false);
        this.showError(err || 'Something went wrong');
      },
      complete: () => {
        this.isSectionLoading.set(false);
        this.closeModalViaNativeElement('createSectionTitleModal');
        this.getSurveyInfo();
      },
    });
  }

  openEditSectionModal(section: any) {
    this.isEditSectionMode.set(true);
    this.sectionId.set(section.sectionId);
    this.sectionTitle.set(section.title);
    this.subTitle.set(section.subTitle || '');
    this.description.set(section.description || '');

  }

  openCreateQuestionModal(sectionId: any) {
    this.sectionId.set(sectionId);

    this.isEditQuestionMode.set(false);
    this.questionId.set(null);
    this.questionTextMy.set('');
    this.questionTextEng.set('');
  }

  //create question for super admin and admin
  createQuestion() {

    let api
    if (this.isEditQuestionMode()) {
      api = this.apiService.updateQuestion(this.questionId(), this.questionTextMy(), this.questionTextEng());
    } else {
      api = this.apiService.createQuestion(this.sectionId(), this.questionTextMy(), this.questionTextEng());
    }

    this.isQuestionLoading.set(true);
    api.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.showSuccess(
          this.isEditQuestionMode()
            ? 'Question Updated Successfully'
            : 'Question Created Successfully'
        );
      },
      error: (err: any) => {
        this.isQuestionLoading.set(false);
        this.showError(err || 'Something went wrong');
      },
      complete: () => {
        this.isQuestionLoading.set(false);
        this.closeModalViaNativeElement('createQuestionModal');
        this.getSurveyInfo();
      },
    });
  }

  openEditQuestionModal(q: any) {
    this.isEditQuestionMode.set(true);
    this.questionId.set(q.questionId);

    this.questionTextMy.set(q.questionTextMy);
    this.questionTextEng.set(q.questionTextEng);
  }

  //delete question for super admin and admin
  onDeleteQuestion(question: any) {
    const confirmDelete = confirm(
      `Are you sure you want to delete "${question.questionTextEng}"?`
    );

    if (!confirmDelete) return;

    this.apiService.deleteQuestion(question.questionId).subscribe({
      next: () => {
        this.showSuccess('Question deleted successfully');
      },
      error: (err: any) => this.showError(err || 'Failed to delete question.'),
      complete: () => {
        this.getSurveyInfo();
      },
    });
  }

  activeSurvey() {
    this.isBtnLoading.set(true);
    this.apiService.updateSurveyFormStatus(this.surveyId(), SurveyStatus.ACTIVE).subscribe({
      next: () => {
        this.showSuccess('Survey Form Status Active Successfully');
      },
      error: (err: any) => {
        this.showError(err || 'Failed to activate survey.');
      },
      complete: () => {
        this.isBtnLoading.set(false);
        this.getSurveyInfo();
      },
    });
  }
  getRatingScore(ratingId: any): number {
    if (!ratingId) return 99;
    const match = this.ratings().find((r: any) => r.ratingId === ratingId);
    return match ? match.score : 99;
  }

  // 2. Updated Validation to Enforce Remarks Requirement
  validateAllAnswered(): boolean {
    for (const group of this.groupedSections()) {
      for (const section of group.sectionInfoRespLst || []) {
        for (const q of section.questionInfoRespLst || []) {

          // Check 1: Must select a rating radio choice option
          // if (!q.ratingId) {
          //   alert(`Please answer the question :\n"${q.questionTextEng}"`);
          //   return false;
          // }

          // Check 2: If score is under 12, remark text value cannot be empty
          const score = this.getRatingScore(q.ratingId);
          if (score < 12 && (!q.remark || !q.remark.trim())) {
            alert(`Remarks are mandatory for low score ratings! Please fill remarks for:\n"${q.questionTextEng}"`);
            return false;
          }
        }
      }
    }
    return true;
  }

  // 3. Updated Payload Builder to push the remark parameter
  buildPayload() {
    const payload: any[] = [];

    for (const group of this.groupedSections()) {
      for (const section of group.sectionInfoRespLst || []) {
        for (const q of section.questionInfoRespLst || []) {
          payload.push({
            questionId: q.questionId,
            ratingId: q.ratingId,
            remark: this.getRatingScore(q.ratingId) < 12 ? q.remark?.trim() : null // Only forward clean values if score dictates it
          });
        }
      }
    }

    return payload;
  }

  submitSurvey() {
    if (!this.validateAllAnswered()) return;

    const requestBody = {
      surveyId: this.surveyId(),
      suggestions: this.suggestions(),
      answers: this.buildPayload(),
    };

    this.isBtnLoading.set(true);

    this.apiService.getSubmitSurvey(requestBody).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.isBtnLoading.set(false))
    ).subscribe({
      next: () => {
        this.showSuccess('Survey answered successfully');
        this.getSurveyInfo();
      },
      error: (err: any) => {
        this.showError(err || 'Failed to submit survey.');
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/nav/survey'], { skipLocationChange: true });
  }

  showError(msg: string): void {
    this.errorMessage.set(msg);
    setTimeout(() => this.clearAlerts(), 6000);
  }

  showSuccess(msg: string): void {
    this.successMessage.set(msg);
    setTimeout(() => this.clearAlerts(), 4000);
  }

  clearAlerts(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }

}
